import type { BoxProps } from '@chakra-ui/react';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import type { CardCarouselProps } from '@time-line/components/common';
import { CardCarousel, Grid } from '@time-line/components/common';
import { BannerCard } from 'components/common/BannerCard';
import { ProductCard } from 'components/product/ProductCard';
import { useAnalytics } from 'hooks/useAnalytics';
import { usePathname } from 'next/navigation';
import { useCallback } from 'react';
import type { SanityProduct } from 'types/cms/SanityProduct';

type ProductCardCarouselProps = Omit<CardCarouselProps, 'children'> &
  BoxProps & {
    products: SanityProduct[];
    collectionName?: string;
    isSkincareCardVisible?: boolean;
  };

export function ProductCardCarousel({
  collectionName,
  products,
  isSkincareCardVisible,
  ...props
}: ProductCardCarouselProps) {
  const pathname = usePathname();
  const { analytics } = useAnalytics();

  const isLargeScreen = useBreakpointValue(
    { base: false, lg: true },
    { fallback: 'lg' },
  );

  analytics.viewItemList({
    listId: collectionName,
    products: products.map((product) => product.title),
  });

  const handleBannerCardDisplay = useCallback(() => {
    analytics.skincareCardDisplay({ slug: pathname || '' });
  }, [analytics, pathname]);

  const slides = [
    ...products.map((product, i) => (
      <ProductCard
        key={i}
        {...product}
        isATCVisible
        collectionName={collectionName}
        index={i}
      />
    )),
    ...(isSkincareCardVisible && isLargeScreen
      ? [
          <BannerCard
            key="banner-card"
            mb="0"
            onDisplay={handleBannerCardDisplay}
          />,
        ]
      : []),
  ];

  return (
    <>
      <CardCarousel
        className="section--product-carousel"
        buttonVerticalOffset="44%"
        sx={{
          '.swiper-wrapper': {
            alignItems: 'stretch',
            display: 'flex',
          },
          '.swiper-slide': {
            height: 'unset',
          },
        }}
        mb={isSkincareCardVisible ? { base: 'lg', md: '2xl' } : '2xl'}
        {...props}
      >
        {slides}
      </CardCarousel>
      {isSkincareCardVisible && !isLargeScreen && (
        <Grid mb={isSkincareCardVisible ? { base: 'lg', md: '2xl' } : '2xl'}>
          <Box gridColumn="main">
            <BannerCard onDisplay={handleBannerCardDisplay} />
          </Box>
        </Grid>
      )}
    </>
  );
}
